import { createAlova } from 'alova';
// @ts-ignore - GlobalFetch 使用 CommonJS 导出
import GlobalFetch from 'alova/GlobalFetch';
import SvelteHook from 'alova/svelte';

import { clusterReplaceUrl } from './clusterReplaceHost';
import { getSign, initSCG, MARKET_BIZ_ID } from './getSign';
import type { MethodRequestConfig } from './service.d';
import { requestSendTrack, requestEndSendTrack, requestHttpErrorTrack, requestServerErrorTrack } from './serviceTrack';

import { ENV } from '@/configs/env';
import { createTrackDone } from '@/configs/errorMonitor';
import { isDidifinanceUrl } from '@/utils/cluster';
import { getCookie } from '@/utils/cookie';
import { trackError, trackEvent } from '@/utils/omega';
import { isApp } from '@/utils/os';

initSCG({
    bizId: MARKET_BIZ_ID,
    appVer: 'v1.0'
});

interface Params {
    [key: string]: unknown;
}

interface HttpRequestConfig {
    timeout?: number;
    headers?: Record<string, string>;
    params?: Params;
}

const alovaInstance = createAlova({
    requestAdapter: GlobalFetch(),
    statesHook: SvelteHook,
    timeout: 10000,
    beforeRequest(method) {
        method.url = clusterReplaceUrl(method.url);

        // 加入验签能力
        const contentType = method.config.headers['Content-Type'] || 'application/json';

        if (isDidifinanceUrl(method.url)) {
            if (isApp()) {
                if (window.fusionOid) {
                    method.config.headers['oid'] = window.fusionOid;
                }
            } else {
                const oid = getCookie('oid');

                if (oid) {
                    method.config.headers['oid'] = oid;
                }
            }
        }

        const params = method.config.params;
        const data = method.data;
        const timestamp = Date.now();

        const funObj = {
            params: params,
            body: data,
            contentType: contentType
        };

        const sign = getSign(funObj);

        if (sign && process.env.BUILD_ENV === ENV.DEV) {
            method.url = `${method.url}?wsgsig=${encodeURIComponent(sign)}&ts=${timestamp}`;
        } else if (sign) {
            method.url = `${method.url}?wsgsig=${encodeURIComponent(sign)}`;
        }

        requestSendTrack(method);
    },
    responded: {
        onSuccess: async (response, method) => {
            const { trackDone } = method.meta;
            const res = response as Response || {};

            if (res.status !== 200) {
                if (window.errorReportInit) {
                    trackDone(false, undefined, response);
                }

                requestServerErrorTrack(response as Response, method);
            }

            if (res.status >= 400) {
                throw new Error(res.statusText);
            }

            const json = await res.json();

            requestEndSendTrack(res, method, json);

            if (window.errorReportInit) {
                trackDone(true, {
                    url: res.url,
                    headers: res.headers,
                    statusText: res.statusText,
                    status: res.status,
                    data: json
                }, undefined);
            }

            return json;
        },
        onError: (err, method) => {
            const { trackDone } = method.meta;

            if (window.errorReportInit) {
                trackDone(false, undefined, err);
            }

            requestHttpErrorTrack(err, method);
            trackError(method.url, 5000, 'REQUEST_ERROR_SVELTE_PROJECT', {
                ...method.config
            });
            throw new Error(err.message);
        },
        onComplete: async () => {
            // 处理请求完成逻辑
        }
    }
});
const reportPerformanceData = (url: string, timeStart: number) => {
    const timeEnd = window.performance ? window.performance.now() : Date.now();

    // 性能埋点监控

    if (timeEnd - timeStart) {
        window.performance && window.performance.mark('fmp'); // 有意义渲染时间(FMP) mas fmp统计用
        try {
            const newUrl = new URL(
                /https?/.test(url)
                    ? url
                    : `${window.location.protocol}//${url.replace(/^\/\//, '')}`
            );

            trackEvent('tech_performance_custom', {
                timingCategory: newUrl.href,
                timingName: newUrl.pathname,
                timingValue: timeEnd - timeStart
            });
        } catch (e) {
            console.log(e);
        }
    }
};

export const httpGet = async (url: string, params: Partial<MethodRequestConfig>) => {
    const timeStart = window.performance
        ? window.performance.now()
        : Date.now();
    const trackDone = createTrackDone(url, params);
    const res = await alovaInstance.Get(url, {
        params,
        meta: {
            timeStart,
            trackDone
        }
    });

    reportPerformanceData(url, timeStart);

    return res;
};

export const httpPost = async (url: string, params: Params, config?: HttpRequestConfig) => {
    const timeStart = window.performance
        ? window.performance.now()
        : Date.now();
    const trackDone = createTrackDone(url, params);

    const res = await alovaInstance.Post(url, params, {
        meta: {
            timeStart,
            trackDone
        },
        ... config
    });

    reportPerformanceData(url, timeStart);

    return res;
};

export const httpPostForm = async (url: string, params: Params) => {
    const timeStart = window.performance
        ? window.performance.now()
        : Date.now();
    const trackDone = createTrackDone(url, params);

    const formData = new FormData();

    for (const key in params) {
        formData.append(key, String(params[key]));
    }

    const config = {
        meta: {
            timeStart,
            trackDone
        }
    };

    const res = await alovaInstance.Post(url, formData, config);

    reportPerformanceData(url, timeStart);

    return res;
};

export default alovaInstance;
